import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import PublicHub from "./pages/PublicHub";
import PublicLandingPage from "./pages/public/PublicLandingPage";
import PublicThankYou from "./pages/public/PublicThankYou";
import MagicLinkVerify from "./pages/MagicLinkVerify";

import DashboardLayout from "./pages/DashboardLayout";
import CommandCenter from "./pages/CommandCenter";
import Onboarding from "./pages/Onboarding";
import Admin from "./pages/Admin";
import RecoveryCenter from "./pages/RecoveryCenter";

// Creator OS sections
import Capture from "./pages/sections/Capture";
import Convert from "./pages/sections/Convert";
import Insights from "./pages/sections/Intelligence";
import Forge from "./pages/sections/Forge";
import Experiences from "./pages/sections/Experiences";
import Amplify from "./pages/sections/Amplify";
import Nexus from "./pages/sections/Nexus";
import Settings from "./pages/sections/Settings";
import EmailCampaignsPage from "./pages/email/EmailCampaignsPage";

// Legal
import TermsPage from "./pages/legal/TermsPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import RefundPolicyPage from "./pages/legal/RefundPolicyPage";

// Usage + Admin (Phase G)
import UsagePage from "./pages/usage/UsagePage";
import AdminCostDashboard from "./pages/admin/AdminCostDashboard";

// Growth Hub (Phase J)
import BlogIndex, { BlogArticle } from "./pages/blog/BlogIndex";

// Customer Portal
import CustomerPortalLayout from "./pages/customer/CustomerPortalLayout";
import CustomerOverview from "./pages/customer/CustomerOverview";
import CustomerLibrary from "./pages/customer/CustomerLibrary";
import { CustomerExperiences, CustomerMemberships, CustomerPurchases } from "./pages/customer/CustomerPortalPages";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300" data-testid="loading-screen">Loading Ashra…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/magic-link/verify" element={<MagicLinkVerify />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

      {/* Creator OS — single unified workspace */}
      <Route element={<Protected><DashboardLayout /></Protected>}>
        <Route path="/dashboard"   element={<CommandCenter />} />
        <Route path="/capture"     element={<Capture />} />
        <Route path="/convert"     element={<Convert />} />
        <Route path="/recover"     element={<RecoveryCenter />} />
        <Route path="/insights"    element={<Insights />} />
        <Route path="/forge"       element={<Forge />} />
        <Route path="/experiences" element={<Experiences />} />
        <Route path="/amplify"     element={<Amplify />} />
        <Route path="/nexus"       element={<Nexus />} />
        <Route path="/settings"    element={<Settings />} />
        <Route path="/email/campaigns" element={<EmailCampaignsPage />} />
        <Route path="/usage"           element={<UsagePage />} />
      </Route>

      {/* Admin Portal — full-screen, own sidebar */}
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/cost" element={<Protected><AdminCostDashboard /></Protected>} />

      {/* Customer Portal — fully separate */}
      <Route path="/customer" element={<Protected><CustomerPortalLayout /></Protected>}>
        <Route index element={<CustomerOverview />} />
        <Route path="library"     element={<CustomerLibrary />} />
        <Route path="experiences" element={<CustomerExperiences />} />
        <Route path="memberships" element={<CustomerMemberships />} />
        <Route path="purchases"   element={<CustomerPurchases />} />
        {/* legacy children */}
        <Route path="courses"       element={<Navigate to="/customer/library" replace />} />
        <Route path="orders"        element={<Navigate to="/customer/purchases" replace />} />
        <Route path="subscriptions" element={<Navigate to="/customer/memberships" replace />} />
        <Route path="events"        element={<Navigate to="/customer/experiences" replace />} />
      </Route>

      {/* Backward-compat redirects (legacy URLs → new sections) */}
      <Route path="/inbox"         element={<Navigate to="/capture?tab=inbox" replace />} />
      <Route path="/leads"         element={<Navigate to="/capture?tab=leads" replace />} />
      <Route path="/opportunities" element={<Navigate to="/capture?tab=opportunities" replace />} />
      <Route path="/flows"         element={<Navigate to="/forge?tab=funnels" replace />} />
      <Route path="/automations"   element={<Navigate to="/convert?tab=workforce" replace />} />
      <Route path="/copilot"       element={<Navigate to="/convert?tab=strategist" replace />} />
      <Route path="/recovery"      element={<Navigate to="/recover" replace />} />
      <Route path="/analytics"     element={<Navigate to="/insights?tab=analytics" replace />} />
      <Route path="/attribution"   element={<Navigate to="/insights?tab=attribution" replace />} />
      <Route path="/forecast"      element={<Navigate to="/insights?tab=forecast" replace />} />
      <Route path="/alerts"        element={<Navigate to="/insights?tab=alerts" replace />} />
      <Route path="/intelligence"  element={<Navigate to="/insights" replace />} />
      <Route path="/training"      element={<Navigate to="/nexus" replace />} />
      <Route path="/ai-brain"      element={<Navigate to="/nexus" replace />} />
      <Route path="/billing"       element={<Navigate to="/settings?tab=billing" replace />} />
      <Route path="/integrations"  element={<Navigate to="/settings?tab=integrations" replace />} />
      <Route path="/products"      element={<Navigate to="/experiences?tab=products" replace />} />
      <Route path="/webinars"      element={<Navigate to="/experiences?tab=webinars" replace />} />
      <Route path="/affiliates"    element={<Navigate to="/amplify?tab=affiliates" replace />} />
      <Route path="/portal"        element={<Navigate to="/customer" replace />} />
      {/* Old commerce.* URLs map into Experiences */}
      <Route path="/commerce"               element={<Navigate to="/experiences?tab=hub" replace />} />
      <Route path="/commerce/products"      element={<Navigate to="/experiences?tab=products" replace />} />
      <Route path="/commerce/courses"       element={<Navigate to="/experiences?tab=courses" replace />} />
      <Route path="/commerce/memberships"   element={<Navigate to="/experiences?tab=memberships" replace />} />
      <Route path="/commerce/webinars"      element={<Navigate to="/experiences?tab=webinars" replace />} />
      <Route path="/commerce/community"     element={<Navigate to="/experiences?tab=community" replace />} />
      <Route path="/commerce/bookings"      element={<Navigate to="/experiences?tab=bookings" replace />} />
      <Route path="/commerce/affiliates"    element={<Navigate to="/amplify?tab=affiliates" replace />} />
      <Route path="/commerce/customers"     element={<Navigate to="/experiences?tab=customers" replace />} />

      {/* Public landing pages (must be before catch-all handle route) */}
      <Route path="/p/:handle/:slug/thanks" element={<PublicThankYou />} />
      <Route path="/p/:handle/:slug" element={<PublicLandingPage />} />

      {/* Public hub at root-level handle (must be after named routes) */}
      <Route path="/:handle" element={<PublicHub />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" theme="dark" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
